import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import type { Rarity } from '#src/__generated__/globalTypes.ts';
import { ConditionalWrapper } from '#src/atoms/layout/ConditionalWrapper/index.tsx';
import type { ValidWidths } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import { proxyUrl } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import { Video } from '#src/atoms/ui/Video/index.tsx';
import {
  CardImg,
  CardImgLoadingWrapper,
} from '#src/components/card/CardImg.tsx';
import { CardMeta } from '#src/components/card/CardMeta/index.tsx';
import { useIsVisibleInViewport } from '#src/hooks/ui/useIsVisibleInViewport.ts';
import { getAnimatedCardVideoSources } from '#src/lib/cards.ts';
import { isType } from '#src/lib/gql/index.ts';

import { Foil } from '../Foil/index.tsx';
import { MountingAnimation } from './MountingAnimation/index.tsx';
import { UnmountingAnimation } from './UnmountingAnimation/index.tsx';
import type {
  Card_anyCard,
  Card_commonPlayer,
  Card_composeTeamBenchObjectInterface,
} from './__generated__/index.graphql.ts';

const RoundedVideo = styled(Video)`
  overflow: clip;
  aspect-ratio: var(--card-aspect-ratio);
`;

type ClickHandlerProps = {
  onClick?: () => void;
};
type IProps = {
  card:
    | Card_anyCard
    | Card_composeTeamBenchObjectInterface
    | (Card_commonPlayer & { rarityTyped?: Rarity });
  width?: ValidWidths;
  animations?: { mounting?: boolean; unmouting?: boolean };
  unmounting?: boolean;
  lazy?: boolean;
};

export const Card = (props: IProps & ClickHandlerProps) => {
  const [loaded, setLoaded] = useState(false);
  const {
    card,
    width = 320,
    onClick,
    animations,
    unmounting,
    lazy = true,
  } = props;
  const { pictureUrl, anyPlayer, rarityTyped } = card;

  const videoSources = isType(card, 'CommonPlayer')
    ? []
    : getAnimatedCardVideoSources(card, width);

  const hasVideo = videoSources.length > 0;
  const { handleRef: videoRef, isVisible: videoIsVisible } =
    useIsVisibleInViewport<HTMLDivElement>({ skip: !hasVideo });
  const withMeta =
    isType(card, 'BaseballCard') ||
    isType(card, 'Card') ||
    isType(card, 'NBACard') ||
    isType(card, 'CommonPlayer');

  const isSealed = 'sealed' in card && card.sealed;

  const mediaElement = hasVideo ? document.createElement('video') : null;
  const useVideoBackground =
    hasVideo &&
    videoSources.filter(e => mediaElement!.canPlayType(e.type) !== '').length >
      0 &&
    !isSealed;

  return (
    <CardImgLoadingWrapper loaded={loaded}>
      {!loaded && !!withMeta && <CardMeta card={card} />}
      <ConditionalWrapper
        Wrapper={MountingAnimation}
        wrap={!!animations?.mounting}
        props={{ id: card.anyPlayer.slug }}
      >
        <ConditionalWrapper
          Wrapper={UnmountingAnimation}
          wrap={!!animations?.unmouting && !!unmounting}
        >
          <div ref={videoRef}>
            {!!useVideoBackground && (!!videoIsVisible || !lazy) && (
              <RoundedVideo
                sources={videoSources}
                autoPlay
                loop
                muted
                onLoadedData={() => setLoaded(true)}
                poster={
                  pictureUrl
                    ? proxyUrl(pictureUrl, { cropWidth: width })
                    : undefined
                }
                playsInline
                hideMutedIcon
                style={{
                  mask: pictureUrl
                    ? `url(${proxyUrl(pictureUrl, { cropWidth: width })}) center / cover no-repeat`
                    : 'none',
                }}
              />
            )}
          </div>
          {!useVideoBackground && (
            <ConditionalWrapper
              wrap={!!card.foilUrl}
              Wrapper={Foil}
              props={{
                maskShape: pictureUrl
                  ? proxyUrl(pictureUrl, { cropWidth: width })
                  : undefined,
                maskFoil: card.foilUrl
                  ? proxyUrl(card.foilUrl, { cropWidth: width })
                  : '',
                foilColor: card.foilColorUrl
                  ? proxyUrl(card.foilColorUrl, { cropWidth: width })
                  : undefined,
              }}
            >
              <CardImg
                {...(lazy ? { loading: 'lazy' } : {})}
                src={pictureUrl || undefined}
                alt={`${anyPlayer.displayName}${rarityTyped ? ` - ${rarityTyped}` : ''}`}
                draggable={false}
                onLoad={() => {
                  setLoaded(true);
                }}
                onClick={onClick}
                width={width}
              />
            </ConditionalWrapper>
          )}
        </ConditionalWrapper>
      </ConditionalWrapper>
    </CardImgLoadingWrapper>
  );
};
Card.displayName = 'Card';

Card.fragments = {
  anyCard: gql`
    fragment Card_anyCard on AnyCardInterface {
      slug
      pictureUrl
      foilUrl: pictureUrl(derivative: "front_foil")
      foilColorUrl: pictureUrl(derivative: "front_foil_color")
      rarityTyped
      sealed
      anyPlayer {
        slug
        displayName
      }
      ...CardMeta_AnyCardInterface
      ...getAnimatedCardVideoSources_anyCardInterface
    }
    ${CardMeta.fragments.AnyCardInterface}
    ${getAnimatedCardVideoSources.fragments.anyCardInterface}
  ` as TypedDocumentNode<Card_anyCard>,
  composeTeamBenchObjectInterface: gql`
    fragment Card_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      pictureUrl
      foilUrl: pictureUrl(derivative: "front_foil")
      foilColorUrl: pictureUrl(derivative: "front_foil_color")
      rarityTyped: rarity
      anyPlayer {
        slug
        displayName
      }
      ...getAnimatedCardVideoSources_composeTeamBenchObjectInterface
    }
    ${getAnimatedCardVideoSources.fragments.composeTeamBenchObjectInterface}
  ` as TypedDocumentNode<Card_composeTeamBenchObjectInterface>,
  commonPlayer: gql`
    fragment Card_commonPlayer on CommonPlayer {
      id
      pictureUrl
      foilUrl: pictureUrl(derivative: "front_foil")
      foilColorUrl: pictureUrl(derivative: "front_foil_color")
      anyPlayer {
        slug
        displayName
        ...CardMeta_anyPlayerInterface
      }
    }
    ${CardMeta.fragments.anyPlayerInterface}
  ` as TypedDocumentNode<Card_commonPlayer>,
};
